import { UserGroupMemberRole } from '@navio/nsdk';
import type { CellContext } from '@tanstack/react-table';
import type { NoddiColumnDef } from 'noddi-ui-web';
import { NoddiBadge } from 'noddi-ui-web';
import { CarDriverSummary } from './CarDriverSummary';
import { CarIdentity } from './CarIdentity';
import { DrivenCount } from './DrivenCount';
import { MemberContactInfo } from './MemberContactInfo';
import { PRIMARY_CONTACT_COLUMN_DESCRIPTION, PRIMARY_CONTACT_LABEL } from './memberSheetCopy';
import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

export const getCarColumns = (
  t: NotificationGroupOverviewTranslations,
  isMobile: boolean
): NoddiColumnDef<CarGroup>[] =>
  isMobile
    ? [
        {
          header: t.car,
          id: 'car',
          cell: ({ row }: CellContext<CarGroup, unknown>) => (
            <div className='flex min-w-0 flex-col gap-1.5'>
              <CarIdentity car={row.original} />
              <CarDriverSummary car={row.original} translations={t} />
            </div>
          )
        }
      ]
    : [
        {
          header: t.car,
          accessorKey: 'make',
          cell: ({ row }: CellContext<CarGroup, unknown>) => <CarIdentity car={row.original} />
        },
        {
          header: t.driver,
          id: 'driver',
          meta: { description: t.driverTooltip },
          cell: ({ row }: CellContext<CarGroup, unknown>) => <CarDriverSummary car={row.original} translations={t} />
        }
      ];

export const getMemberColumns = (
  t: NotificationGroupOverviewTranslations,
  isMobile: boolean,
  {
    showPrimaryContact = false,
    showEmployeeRole = false
  }: { showPrimaryContact?: boolean; showEmployeeRole?: boolean } = {}
): NoddiColumnDef<MemberGroup>[] =>
  isMobile
    ? [
        {
          header: t.member,
          id: 'member',
          cell: ({ row }: CellContext<MemberGroup, unknown>) => (
            <MemberContactInfo
              firstName={row.original.firstName}
              lastName={row.original.lastName}
              phoneNumber={row.original.phoneNumber}
              adminLabel={row.original.isAdmin ? t.groupAdmin : undefined}
              employeeLabel={
                showEmployeeRole && row.original.role === UserGroupMemberRole.EMPLOYEE ? t.employee : undefined
              }
            />
          )
        },
        // Its own column rather than a value tucked into the member cell: a right-aligned
        // number with no header above it reads as unlabelled.
        {
          header: t.drives,
          id: 'cars',
          meta: { description: t.drivesTooltip, descriptionClassName: 'max-w-80' },
          cell: ({ row }: CellContext<MemberGroup, unknown>) => <DrivenCount member={row.original} translations={t} />
        }
      ]
    : [
        {
          header: t.member,
          accessorKey: 'firstName',
          cell: ({ row }: CellContext<MemberGroup, unknown>) => (
            <MemberContactInfo
              firstName={row.original.firstName}
              lastName={row.original.lastName}
              email={row.original.email}
            />
          )
        },
        {
          header: t.phoneNumber,
          accessorKey: 'phoneNumber',
          meta: { type: 'phone' }
        },
        {
          // A badge under the name crowds the row on desktop, so the role gets its
          // own column, and the header tooltip spells out what the role grants.
          header: t.groupAdmin,
          id: 'isAdmin',
          accessorKey: 'isAdmin',
          meta: { description: t.groupAdminDescription, descriptionClassName: 'max-w-80' },
          cell: ({ row }: CellContext<MemberGroup, unknown>) =>
            row.original.isAdmin && <NoddiBadge variant='outline'>{t.groupAdmin}</NoddiBadge>
        },
        ...(showEmployeeRole
          ? [
              {
                header: t.employee,
                id: 'role',
                accessorKey: 'role',
                meta: { description: t.employeeDescription, descriptionClassName: 'max-w-80' },
                cell: ({ row }: CellContext<MemberGroup, unknown>) =>
                  row.original.role === UserGroupMemberRole.EMPLOYEE && (
                    <NoddiBadge variant='outline'>{t.employee}</NoddiBadge>
                  )
              } satisfies NoddiColumnDef<MemberGroup>
            ]
          : []),
        ...(showPrimaryContact
          ? [
              {
                header: PRIMARY_CONTACT_LABEL,
                id: 'isPrimaryContact',
                accessorKey: 'isPrimaryContact',
                meta: { description: PRIMARY_CONTACT_COLUMN_DESCRIPTION, descriptionClassName: 'max-w-80' },
                cell: ({ row }: CellContext<MemberGroup, unknown>) =>
                  row.original.isPrimaryContact && <NoddiBadge variant='info'>{PRIMARY_CONTACT_LABEL}</NoddiBadge>
              } satisfies NoddiColumnDef<MemberGroup>
            ]
          : []),
        {
          header: t.drives,
          id: 'cars',
          meta: { description: t.drivesTooltip, descriptionClassName: 'max-w-80' },
          cell: ({ row }: CellContext<MemberGroup, unknown>) => <DrivenCount member={row.original} translations={t} />
        }
      ];
